/* =====================================================================================
   Projects: the scheduling model behind the Gantt. The database holds the invariants
   (no dependency cycles, no cross-project links, milestones are points); this module
   holds the arithmetic: the critical-path method over finish-to-start links with lag,
   and the cascade that shifts successors when a predecessor slips.
   ===================================================================================== */

import { BACKEND } from './db';
import { explain } from './dberrors';

const live = () => BACKEND === 'supabase';

export interface Project {
  id: string; number: string; name: string; description: string | null;
  status: string; colour: string; owner_id: string | null;
  baseline_set_at: string | null;
}
export interface Task {
  id: string; project_id: string; parent_id: string | null; name: string;
  starts_on: string; ends_on: string; is_milestone: boolean; percent_complete: number;
  assignee_id: string | null; sort_order: number; notes: string | null;
  baseline_starts_on: string | null; baseline_ends_on: string | null;
}
export interface Dep { id: string; task_id: string; depends_on_task_id: string; lag_days: number }
export interface Member { id: string; full_name: string }

export const PROJECT_STATUSES: [string, string][] = [
  ['planning', 'Planning'], ['active', 'Active'], ['on_hold', 'On hold'],
  ['done', 'Done'], ['archived', 'Archived'],
];
export const PROJECT_COLOURS = ['#4C6FBF', '#1B9AA8', '#7A5CC4', '#B4635A', '#5B8C5A', '#B08A3E'];

async function sb() { const { supabase } = await import('./supabase'); return supabase(); }

export async function fetchMembers(): Promise<Member[]> {
  const { data, error } = await (await sb()).schema('app').from('members')
    .select('id, full_name').eq('status', 'active').order('full_name');
  if (error) throw new Error(explain(error.message, 'read the team'));
  return (data ?? []) as Member[];
}
export async function currentMemberId(): Promise<string | null> {
  const { data } = await (await sb()).schema('app').rpc('current_member_id');
  return (data as string) ?? null;
}

export async function fetchProjects(): Promise<Project[]> {
  if (!live()) return [];
  const { data, error } = await (await sb()).schema('app').from('projects')
    .select('*').order('created_at', { ascending: false });
  if (error) throw new Error(explain(error.message, 'read the projects'));
  return (data ?? []) as Project[];
}
export async function createProject(p: { name: string; ownerId?: string; colour?: string;
  description?: string }): Promise<string> {
  const s = await sb();
  const { data: org } = await s.schema('app').rpc('current_org_id');
  const { data: number, error: eN } = await s.schema('app')
    .rpc('next_number', { p_org_id: org, p_series: 'project' });
  if (eN) throw new Error(explain(eN.message, 'take the next project number'));
  const { data, error } = await s.schema('app').from('projects').insert({
    number, name: p.name, owner_id: p.ownerId || null,
    colour: p.colour || PROJECT_COLOURS[0], description: p.description || null,
  }).select('id').single();
  if (error) throw new Error(explain(error.message, 'create the project'));
  return data.id as string;
}
export async function updateProject(id: string, patch: Partial<Project>) {
  const { error } = await (await sb()).schema('app').from('projects')
    .update(patch).eq('id', id);
  if (error) throw new Error(explain(error.message, 'update the project'));
}
export async function setBaseline(projectId: string): Promise<number> {
  const { data, error } = await (await sb()).schema('app')
    .rpc('set_project_baseline', { p_project: projectId });
  if (error) throw new Error(explain(error.message, 'set the baseline'));
  return data as number;
}

export async function fetchTasks(projectId: string): Promise<Task[]> {
  const { data, error } = await (await sb()).schema('app').from('project_tasks')
    .select('*').eq('project_id', projectId)
    .order('sort_order').order('created_at');
  if (error) throw new Error(explain(error.message, 'read the plan'));
  return (data ?? []) as Task[];
}
export async function createTask(t: { projectId: string; parentId?: string; name: string;
  startsOn: string; endsOn: string; isMilestone?: boolean; assigneeId?: string;
  sortOrder?: number }): Promise<string> {
  const { data, error } = await (await sb()).schema('app').from('project_tasks').insert({
    project_id: t.projectId, parent_id: t.parentId || null, name: t.name,
    starts_on: t.startsOn, ends_on: t.isMilestone ? t.startsOn : t.endsOn,
    is_milestone: t.isMilestone ?? false, assignee_id: t.assigneeId || null,
    sort_order: t.sortOrder ?? 0,
  }).select('id').single();
  if (error) throw new Error(explain(error.message, 'add the task'));
  return data.id as string;
}
export async function updateTask(id: string, patch: Partial<Task>) {
  const { error } = await (await sb()).schema('app').from('project_tasks')
    .update(patch).eq('id', id);
  if (error) throw new Error(explain(error.message, 'update the task'));
}
export async function updateTasksBulk(rows: { id: string; starts_on: string; ends_on: string }[]) {
  const s = await sb();
  for (const r of rows) {
    const { error } = await s.schema('app').from('project_tasks')
      .update({ starts_on: r.starts_on, ends_on: r.ends_on }).eq('id', r.id);
    if (error) throw new Error(explain(error.message, 'move the tasks'));
  }
}
export async function deleteTask(id: string) {
  const { error } = await (await sb()).schema('app').from('project_tasks')
    .delete().eq('id', id);
  if (error) throw new Error(explain(error.message, 'delete the task'));
}

export async function fetchDeps(projectId: string): Promise<Dep[]> {
  const { data, error } = await (await sb()).schema('app').from('project_task_deps')
    .select('id, task_id, depends_on_task_id, lag_days')
    .in('task_id', (await fetchTasks(projectId)).map((t) => t.id));
  if (error) throw new Error(explain(error.message, 'read the dependencies'));
  return (data ?? []) as Dep[];
}
export async function addDep(taskId: string, dependsOn: string, lagDays = 0) {
  const { error } = await (await sb()).schema('app').from('project_task_deps')
    .insert({ task_id: taskId, depends_on_task_id: dependsOn, lag_days: lagDays });
  if (error) throw new Error(explain(error.message, 'link the tasks'));
}
export async function removeDep(id: string) {
  const { error } = await (await sb()).schema('app').from('project_task_deps')
    .delete().eq('id', id);
  if (error) throw new Error(explain(error.message, 'unlink the tasks'));
}

/* ------------------------------------------------------------------ date arithmetic */
export const D = 864e5;
/* All date arithmetic in UTC. Parsing local and serialising UTC loses a day whenever the
   clock is ahead of UTC (all of British Summer Time): found when a 2-day cascade shift
   moved a task 1 day. Local time is for DISPLAY only. */
export const toDate = (s: string) => new Date(s + 'T00:00:00Z');
export const toISO = (d: Date) => d.toISOString().slice(0, 10);
export const addDays = (s: string, n: number) => toISO(new Date(toDate(s).getTime() + n * D));
export const daysBetween = (a: string, b: string) =>
  Math.round((toDate(b).getTime() - toDate(a).getTime()) / D);
export const durationDays = (t: Task) => daysBetween(t.starts_on, t.ends_on) + 1;

/* ------------------------------------------------------------------ critical path
   Classic CPM over finish-to-start links with lag, in days. Parents (summary rows)
   are excluded: their span is their children's. Returns the set of task ids with zero
   total float, plus each task's float for the tooltip. */
export function criticalPath(tasks: Task[], deps: Dep[]): { critical: Set<string>; float: Map<string, number> } {
  const parents = new Set(tasks.filter((t) => t.parent_id).map((t) => t.parent_id as string));
  const nodes = tasks.filter((t) => !parents.has(t.id));
  const byId = new Map(nodes.map((t) => [t.id, t]));
  const preds = new Map<string, Dep[]>();
  const succs = new Map<string, Dep[]>();
  for (const d of deps) {
    if (!byId.has(d.task_id) || !byId.has(d.depends_on_task_id)) continue;
    (preds.get(d.task_id) ?? preds.set(d.task_id, []).get(d.task_id)!).push(d);
    (succs.get(d.depends_on_task_id) ?? succs.set(d.depends_on_task_id, []).get(d.depends_on_task_id)!).push(d);
  }
  // forward pass: earliest finish measured in absolute days from an epoch
  const ef = new Map<string, number>();
  const es = new Map<string, number>();
  const visit = (id: string, stack: Set<string>): number => {
    if (ef.has(id)) return ef.get(id)!;
    if (stack.has(id)) return 0;               // cycles are refused by the DB; belt and braces
    stack.add(id);
    const t = byId.get(id)!;
    const dur = durationDays(t);
    let start = toDate(t.starts_on).getTime() / D;
    for (const d of preds.get(id) ?? []) {
      const predEf = visit(d.depends_on_task_id, stack);
      start = Math.max(start, predEf + 1 + d.lag_days);
    }
    es.set(id, start);
    const fin = start + dur - 1;
    ef.set(id, fin);
    stack.delete(id);
    return fin;
  };
  for (const t of nodes) visit(t.id, new Set());
  const projectEnd = Math.max(...[...ef.values()], 0);
  // backward pass
  const lf = new Map<string, number>();
  const back = (id: string, stack: Set<string>): number => {
    if (lf.has(id)) return lf.get(id)!;
    if (stack.has(id)) return projectEnd;
    stack.add(id);
    const out = succs.get(id) ?? [];
    let fin = projectEnd;
    for (const d of out) {
      const succLs = back(d.task_id, stack) - durationDays(byId.get(d.task_id)!) + 1;
      fin = Math.min(fin, succLs - 1 - d.lag_days);
    }
    lf.set(id, fin);
    stack.delete(id);
    return fin;
  };
  for (const t of nodes) back(t.id, new Set());
  const critical = new Set<string>();
  const float = new Map<string, number>();
  for (const t of nodes) {
    const f = (lf.get(t.id) ?? 0) - (ef.get(t.id) ?? 0);
    float.set(t.id, f);
    if (f <= 0 && (preds.get(t.id)?.length || succs.get(t.id)?.length)) critical.add(t.id);
  }
  return { critical, float };
}

/* ------------------------------------------------------------------ cascade
   A predecessor slipped: every successor whose finish-to-start constraint is now
   violated moves right by exactly the violation, recursively. Returns the moves to
   persist (the moved task included only if it changed). */
export function cascade(tasks: Task[], deps: Dep[], movedId: string,
  newStart: string, newEnd: string): { id: string; starts_on: string; ends_on: string }[] {
  const byId = new Map(tasks.map((t) => [t.id, { ...t }]));
  const t0 = byId.get(movedId);
  if (!t0) return [];
  t0.starts_on = newStart; t0.ends_on = newEnd;
  const succs = new Map<string, Dep[]>();
  for (const d of deps) {
    (succs.get(d.depends_on_task_id) ?? succs.set(d.depends_on_task_id, []).get(d.depends_on_task_id)!).push(d);
  }
  const moved = new Map<string, { starts_on: string; ends_on: string }>();
  moved.set(movedId, { starts_on: newStart, ends_on: newEnd });
  const queue = [movedId];
  while (queue.length) {
    const id = queue.shift()!;
    const pred = byId.get(id)!;
    for (const d of succs.get(id) ?? []) {
      const s = byId.get(d.task_id);
      if (!s) continue;
      const earliest = addDays(pred.ends_on, 1 + d.lag_days);
      if (toDate(s.starts_on) < toDate(earliest)) {
        const shift = daysBetween(s.starts_on, earliest);
        s.starts_on = addDays(s.starts_on, shift);
        s.ends_on = addDays(s.ends_on, shift);
        moved.set(s.id, { starts_on: s.starts_on, ends_on: s.ends_on });
        queue.push(s.id);
      }
    }
  }
  return [...moved.entries()].map(([id, v]) => ({ id, ...v }));
}
